import React, { useEffect, useState } from 'react';
import { gatePresentationOpen } from '../common/PresentationScreenGate';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { UserDisplayInfo } from '../../services/authService';
import { type SlideImageStorageUsage } from '../../services/slideImageStorageService';
import { Grade } from '../../types/domain';
import {
  formatDevNetworkTransferSummary,
  formatUserDailyDataUsageSummary,
  formatUserRecentDataUsageDisplay,
  getDevNetworkTransferBurstDisplay,
  resetDevNetworkTransferStats,
  subscribeDevNetworkTransfer,
  syncDevNetworkTransferFromStorage,
} from '../../utils/devNetworkTransferMonitor';
import {
  CLASS_LESSON_ALARM_ATTENTION_EVENT,
  CLASS_LESSON_ALARM_REOPEN_EVENT,
  CLASS_LESSON_ALARM_SETTINGS_CHANGED_EVENT,
  isClassLessonAlarmMasterEnabled,
  type ClassLessonAlarmAttentionDetail,
  type ClassLessonAlarmReopenDetail,
} from '../../utils/classLessonAlarmSettings';
import { APP_DISPLAY_NAME, APP_VERSION } from '../../appMeta';
import UserAccountMenu from './UserAccountMenu';
import MailIconButton from './MailIconButton';
import SoundToggleButton from './SoundToggleButton';
import RemoteBacklogNavButton from './RemoteBacklogNavButton';
import ClassFocusPicker from '../common/ClassFocusPicker';
import BrandLogo from '../BrandLogo';
import { warmDashboardLessonDaySlotsSnapshotForOpen, installMainTabPresentationSnapshotResponder } from '../../utils/dashboardLessonDaySlotsSnapshot';
import {
  installMainTabAssignmentPointsNoteResponder,
  warmAssignmentPointsNoteForOpen,
} from '../../utils/assignmentPointsNoteTabBridge';
import {
  installMainTabPointItemNoteResponder,
  warmPointItemNoteForOpen,
} from '../../utils/pointItemNoteTabBridge';
import { loadNoteSelectedGroupId } from '../../utils/noteSelectedGroupPreference';
import { getClassMemberGradeLayoutsMap } from '../../services/classMembersService';
import { openMainPresentationTab } from '../../utils/openMainPresentationTab';
import RemoteControllerModal from '../presentationRemote/RemoteControllerModal';
import { usePaidPassGate } from '../../contexts/PaidPassGateContext';
import { useTutorialOptional } from '../../contexts/TutorialContext';

interface TopNavProps {
  grades: Grade[];
  currentGradeId: string | null;
  workspaceId: string;
  onGradeChange: (gradeId: string) => void;
  userDisplay: UserDisplayInfo | null;
  theme: 'dark' | 'light';
  onToggleTheme: () => void;
  onUserClick?: () => void;
  onLogout: () => void;
  onOpenClassSettings?: () => void;
  onOpenClassMembersSettings?: () => void;
  onOpenClassLessonAlarmSettings?: () => void;
  onOpenWorksheetTemplateRegistry?: () => void;
  onOpenTutorials?: () => void;
  onMenuToggle?: () => void;
  mobileMenuOpen?: boolean;
  slideImageStorageUsage?: SlideImageStorageUsage | null;
}

const TopNav: React.FC<TopNavProps> = ({
  grades,
  currentGradeId,
  workspaceId,
  onGradeChange,
  userDisplay,
  theme,
  onToggleTheme,
  onUserClick,
  onLogout,
  onOpenClassSettings,
  onOpenClassMembersSettings,
  onOpenClassLessonAlarmSettings,
  onOpenWorksheetTemplateRegistry,
  onOpenTutorials,
  onMenuToggle,
  mobileMenuOpen = false,
  slideImageStorageUsage = null,
}) => {
  const { isFeatureLocked, requireFeature, canUseFeature } = usePaidPassGate();
  const assignmentLocked = isFeatureLocked('assignment_note');
  const pointItemLocked = isFeatureLocked('point_item_note');
  const canWarmPaidNotes = canUseFeature('assignment_note') || canUseFeature('point_item_note');
  const tutorial = useTutorialOptional();
  const location = useLocation();
  const annualProgressActive =
    location.pathname === '/annual-progress-term1' ||
    location.pathname === '/annual-progress-term2';
  const isDev = process.env.NODE_ENV === 'development';
  const [usageLines, setUsageLines] = React.useState(() => ({
    daily: formatUserDailyDataUsageSummary(),
    recent: formatUserRecentDataUsageDisplay(),
    devCum: isDev ? formatDevNetworkTransferSummary() : '',
    devBurst: isDev ? getDevNetworkTransferBurstDisplay() : '',
  }));
  const [alarmReady, setAlarmReady] = React.useState(() =>
    isClassLessonAlarmMasterEnabled(workspaceId)
  );
  /** ≤900px: 클래스명 5글자 축약 + 중앙 아이콘 가로 스크롤 */
  const [isMobileNav, setIsMobileNav] = useState(() =>
    typeof window !== 'undefined' ? window.matchMedia('(max-width: 900px)').matches : false
  );

  useEffect(() => {
    const mq = window.matchMedia('(max-width: 900px)');
    const sync = () => setIsMobileNav(mq.matches);
    sync();
    mq.addEventListener('change', sync);
    return () => mq.removeEventListener('change', sync);
  }, []);
  const [alarmAttention, setAlarmAttention] = React.useState(false);

  React.useEffect(() => {
    setAlarmAttention(false);
  }, [workspaceId]);

  React.useEffect(() => {
    const onAttention = (e: Event) => {
      const d = (e as CustomEvent<ClassLessonAlarmAttentionDetail>).detail;
      if ((d?.workspaceId ?? '').trim() !== workspaceId.trim()) return;
      setAlarmAttention(Boolean(d?.active));
    };
    window.addEventListener(CLASS_LESSON_ALARM_ATTENTION_EVENT, onAttention as EventListener);
    return () => {
      window.removeEventListener(CLASS_LESSON_ALARM_ATTENTION_EVENT, onAttention as EventListener);
    };
  }, [workspaceId]);

  React.useEffect(() => {
    if (!isDev) return undefined;
    const tick = () =>
      setUsageLines({
        daily: formatUserDailyDataUsageSummary(),
        recent: formatUserRecentDataUsageDisplay(),
        devCum: formatDevNetworkTransferSummary(),
        devBurst: getDevNetworkTransferBurstDisplay(),
      });
    tick();
    const unsubscribe = subscribeDevNetworkTransfer(tick);
    // 다른 탭 이그레스가 BroadcastChannel을 놓친 경우에도 포커스 시 LS 재동기화
    const onFocus = () => {
      syncDevNetworkTransferFromStorage();
      tick();
    };
    window.addEventListener('focus', onFocus);
    return () => {
      unsubscribe();
      window.removeEventListener('focus', onFocus);
    };
  }, [isDev]);

  React.useEffect(() => {
    setAlarmReady(isClassLessonAlarmMasterEnabled(workspaceId));
    const onChanged = (e: Event) => {
      const d = (e as CustomEvent<{ workspaceId?: string }>).detail;
      if ((d?.workspaceId ?? '').trim() !== workspaceId.trim()) return;
      setAlarmReady(isClassLessonAlarmMasterEnabled(workspaceId));
    };
    window.addEventListener(CLASS_LESSON_ALARM_SETTINGS_CHANGED_EVENT, onChanged as EventListener);
    return () => {
      window.removeEventListener(
        CLASS_LESSON_ALARM_SETTINGS_CHANGED_EVENT,
        onChanged as EventListener
      );
    };
  }, [workspaceId]);

  React.useEffect(() => {
    return installMainTabPresentationSnapshotResponder(grades);
  }, [grades]);

  React.useEffect(() => {
    const unsubApn = installMainTabAssignmentPointsNoteResponder();
    const unsubPin = installMainTabPointItemNoteResponder();
    return () => {
      unsubApn();
      unsubPin();
    };
  }, []);

  const [remoteOpen, setRemoteOpen] = React.useState(false);
  const closeRemote = React.useCallback(() => setRemoteOpen(false), []);
  /* 메인 프레젠은 이용권별 권한 main_presentation(기본: 모든 이용권 사용) — 유료 전용 기능은 프레젠 탭 내부에서 잠금.
     이용권 확인 중에는 막지 않고, 확정 후 잠긴 경우에만 안내 모달 */
  const openMainPresentation = React.useCallback(() => {
    gatePresentationOpen(() => {
    if (!workspaceId) return;
    if (isFeatureLocked('main_presentation')) {
      requireFeature('main_presentation');
      return;
    }
    const gradeId =
      (currentGradeId ?? '').trim() ||
      grades[0]?.id ||
      '';
    if (gradeId) {
      const warmed = warmDashboardLessonDaySlotsSnapshotForOpen({
        workspaceId,
        gradeId,
        grades,
        viewDate: new Date(),
      });
      if (isDev) console.info('[TopNav] 메인 프레젠 열기 전 스냅샷 워밍', warmed);
      /* 프레젠은 학급 레이아웃·노트 모두 LS/브릿지 캐시만 쓰므로 메인 탭에서 미리 채운다 */
      void getClassMemberGradeLayoutsMap(workspaceId, [gradeId]).then(() => {
        if (isDev) console.info('[TopNav] 학급 레이아웃 워밍', { workspaceId, gradeId });
      });
      if (canWarmPaidNotes) {
      void warmAssignmentPointsNoteForOpen({ workspaceId, gradeId }).then((r) => {
        if (isDev) console.info('[TopNav] 과제 포인트 워밍', r);
      });
      const pointGroupId =
        loadNoteSelectedGroupId('pointItem', workspaceId, gradeId) ?? undefined;
      void warmPointItemNoteForOpen({
        workspaceId,
        gradeId,
        groupId: pointGroupId,
      }).then((r) => {
        if (isDev) console.info('[TopNav] 포인트 아이템 워밍', r);
      });
      }
    }

    const active = tutorial?.activeTutorial;
    const step = active ? active.steps[tutorial.activeStepIndex] : null;
    const handoffTutorial =
      active?.id === 'main-presentation' && step?.id === 'open-main-presentation';
    if (handoffTutorial && active) {
      const nextIdx = active.steps.findIndex((s) => s.id === 'guide-overview');
      const tutorialStep = nextIdx >= 0 ? nextIdx : tutorial.activeStepIndex + 1;
      const opened = openMainPresentationTab({
        workspaceId,
        gradeId: gradeId || undefined,
        tutorialId: 'main-presentation',
        tutorialStep,
      });
      if (!opened) {
        window.alert(
          '팝업이 차단되어 메인 프레젠테이션 탭을 열 수 없어요. 브라우저에서 팝업을 허용한 뒤 다시 시도해 주세요.'
        );
        return;
      }
      // 새 탭에서 guide-overview부터 이어가고, 이 창의 오버레이는 닫는다.
      // (새 탭 bootstrap이 핸드오프 신호를 보내 이중으로 닫히기도 함)
      tutorial.goToStepById('guide-overview');
      tutorial.exitTutorial();
      return;
    }

    openMainPresentationTab({ workspaceId, gradeId: gradeId || undefined });
  });
  }, [workspaceId, currentGradeId, grades, canWarmPaidNotes, tutorial, isFeatureLocked, requireFeature]);

  // Shift + O 단축키로 메인 프레젠테이션 탭 열기
  // (한글 IME가 켜져 있어도 동작하도록 e.key 대신 레이아웃/IME 독립적인 e.code 사용)
  React.useEffect(() => {
    if (!userDisplay) return undefined;
    const onKeyDown = (e: KeyboardEvent) => {
      if (!e.shiftKey || e.ctrlKey || e.metaKey || e.altKey) return;
      if (e.code !== 'KeyO') return;
      if (e.repeat) return;
      const target = e.target as HTMLElement | null;
      if (target) {
        const tag = target.tagName;
        if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || target.isContentEditable) {
          return;
        }
      }
      e.preventDefault();
      e.stopPropagation();
      openMainPresentation();
    };
    document.addEventListener('keydown', onKeyDown, true);
    return () => {
      document.removeEventListener('keydown', onKeyDown, true);
    };
  }, [userDisplay, openMainPresentation]);

  const usageTitle =
    `개발 상세 · ${usageLines.devCum}\n직전 · ${usageLines.devBurst || '대기'}\n더블클릭 시 오늘·세션 집계 초기화`;

  return (
    <header className="cp-topnav">
      <div className="cp-topnav-left">
        <button
          type="button"
          className={`cp-topnav-menu-toggle${mobileMenuOpen ? ' is-open' : ''}`}
          aria-label={mobileMenuOpen ? '사이드 메뉴 닫기' : '사이드 메뉴 열기'}
          aria-expanded={mobileMenuOpen}
          onClick={() => onMenuToggle?.()}
        >
          <span className="cp-topnav-menu-toggle-bar" aria-hidden />
          <span className="cp-topnav-menu-toggle-bar" aria-hidden />
          <span className="cp-topnav-menu-toggle-bar" aria-hidden />
        </button>
        <span className="cp-logo-version-wrap">
          <Link to="/dashboard" className="cp-logo" aria-label={APP_DISPLAY_NAME}>
            <BrandLogo className="cp-brand-logo--nav" />
          </Link>
          <span className="cp-app-version" aria-label={`앱 버전 ${APP_VERSION}`}>
            {APP_VERSION}
          </span>
        </span>
        {grades.length > 0 ? (
          <ClassFocusPicker
            className="cp-topnav-class-picker"
            grades={grades}
            value={currentGradeId}
            onChange={onGradeChange}
            ariaLabel="클래스 선택"
            buttonTitleMaxLength={isMobileNav ? 5 : undefined}
          />
        ) : null}
        {isDev ? (
          <div
            className="cp-topnav-usage-wrap"
            title={usageTitle}
            onDoubleClick={() => {
              resetDevNetworkTransferStats();
              setUsageLines({
                daily: formatUserDailyDataUsageSummary(),
                recent: formatUserRecentDataUsageDisplay(),
                devCum: formatDevNetworkTransferSummary(),
                devBurst: getDevNetworkTransferBurstDisplay(),
              });
            }}
          >
            <div className="cp-topnav-usage cp-topnav-usage-daily">{usageLines.daily}</div>
            <div className="cp-topnav-usage cp-topnav-usage-recent">{usageLines.recent}</div>
          </div>
        ) : null}
      </div>
      <div className="cp-topnav-center">
        {userDisplay ? (
          <button
            type="button"
            className="cp-topnav-clock-btn"
            data-tutorial-pres="open-nav"
            onClick={openMainPresentation}
            title="메인 프레젠테이션 열기 (Shift + O)"
            aria-label="메인 프레젠테이션 새 탭으로 열기 (Shift + O)"
          >
            <img src="/Display.svg" alt="" width={24} height={24} />
          </button>
        ) : null}
        {userDisplay ? (
          <button
            type="button"
            className="cp-topnav-clock-btn cp-topnav-remote-btn"
            onClick={() => setRemoteOpen(true)}
            title="리모트 열기 (열려 있는 메인 프레젠테이션 제어)"
            aria-label="리모트 열기"
            aria-haspopup="dialog"
          >
            <img src="/Remote.svg" alt="" width={24} height={24} />
          </button>
        ) : null}
        <RemoteControllerModal open={remoteOpen} onClose={closeRemote} />
        {userDisplay && onOpenClassLessonAlarmSettings ? (
          <button
            type="button"
            className={`cp-topnav-alarm-bell${alarmReady ? ' is-ready' : ' is-off'}${
              alarmAttention ? ' is-attention' : ''
            }`}
            data-tutorial-alarm="open-bell"
            onClick={() => {
              if (alarmAttention) {
                window.dispatchEvent(
                  new CustomEvent(CLASS_LESSON_ALARM_REOPEN_EVENT, {
                    detail: { workspaceId } satisfies ClassLessonAlarmReopenDetail,
                  })
                );
                return;
              }
              onOpenClassLessonAlarmSettings();
            }}
            title={
              alarmAttention
                ? '수업 알람 다시 열기'
                : alarmReady
                  ? '수업 알람 준비 중'
                  : '수업 알람 꺼짐'
            }
            aria-label={
              alarmAttention
                ? '수업 알람 다시 열기'
                : alarmReady
                  ? '수업 알람 준비 중 — 설정 열기'
                  : '수업 알람 꺼짐 — 설정 열기'
            }
          >
            <img
              src={
                alarmAttention
                  ? '/alarmring.svg'
                  : alarmReady
                    ? '/alram.svg'
                    : '/alarmoff.svg'
              }
              alt=""
              width={20}
              height={20}
            />
          </button>
        ) : null}
        {userDisplay && onOpenClassLessonAlarmSettings ? (
          <span className="cp-topnav-center-divider" aria-hidden="true" />
        ) : null}
        {userDisplay ? (
          <>
            <NavLink
              to="/assignment-note"
              end
              className={({ isActive }) =>
                `cp-topnav-quick-link${assignmentLocked ? ' is-pass-locked' : ''}${
                  isActive ? ' is-active' : ''
                }`
              }
              onClick={(e) => {
                if (requireFeature('assignment_note')) e.preventDefault();
              }}
              title={
                assignmentLocked ? '이용권 구매 후 이용할 수 있습니다' : '과제 노트'
              }
              aria-label="과제 노트"
              aria-disabled={assignmentLocked || undefined}
            >
              <span
                className="cp-topnav-quick-link-icon cp-topnav-quick-link-icon--task cp-topnav-quick-link-icon--rainbow"
                style={
                  {
                    ['--cp-icon-mask' as string]: `url(${process.env.PUBLIC_URL || ''}/task2.svg)`,
                  } as React.CSSProperties
                }
                aria-hidden="true"
              />
            </NavLink>
            <NavLink
              to="/point-item-note"
              end
              className={({ isActive }) =>
                `cp-topnav-quick-link${pointItemLocked ? ' is-pass-locked' : ''}${
                  isActive ? ' is-active' : ''
                }`
              }
              onClick={(e) => {
                if (requireFeature('point_item_note')) e.preventDefault();
              }}
              title={
                pointItemLocked
                  ? '이용권 구매 후 이용할 수 있습니다'
                  : '포인트 노트'
              }
              aria-label="포인트 노트"
              aria-disabled={pointItemLocked || undefined}
            >
              <span
                className="cp-topnav-quick-link-icon cp-topnav-quick-link-icon--point cp-topnav-quick-link-icon--rainbow"
                style={
                  {
                    ['--cp-icon-mask' as string]: `url(${process.env.PUBLIC_URL || ''}/point.svg)`,
                  } as React.CSSProperties
                }
                aria-hidden="true"
              />
            </NavLink>
            <NavLink
              to="/annual-timetable"
              end
              className={({ isActive }) =>
                `cp-topnav-quick-link${isActive ? ' is-active' : ''}`
              }
              title="연간시간표"
              aria-label="연간시간표"
            >
              <img src={`${process.env.PUBLIC_URL}/table.svg`} alt="" width={20} height={20} />
            </NavLink>
            <button
              type="button"
              className={`cp-topnav-quick-link${annualProgressActive ? ' is-active' : ''}`}
              onClick={() => {
                document.dispatchEvent(
                  new KeyboardEvent('keydown', {
                    key: 'R',
                    code: 'KeyR',
                    shiftKey: true,
                    bubbles: true,
                    cancelable: true,
                  })
                );
              }}
              title="연간진도표 (오늘 날짜 행 선택)"
              aria-label="오늘 날짜의 연간진도표 열기"
            >
              <img
                src={`${process.env.PUBLIC_URL || ''}/Year_Table.svg?v=${encodeURIComponent(APP_VERSION)}`}
                alt=""
                width={20}
                height={20}
              />
            </button>
          </>
        ) : null}
      </div>
      <div className="cp-topnav-right">
        <RemoteBacklogNavButton />
        {userDisplay ? <MailIconButton /> : null}
        {userDisplay ? <SoundToggleButton /> : null}
        {userDisplay ? (
          <UserAccountMenu
            userDisplay={userDisplay}
            workspaceId={workspaceId}
            slideImageStorageUsage={slideImageStorageUsage}
            theme={theme}
            onToggleTheme={onToggleTheme}
            onOpenProfile={() => onUserClick?.()}
            onOpenClassSettings={onOpenClassSettings}
            onOpenClassMembersSettings={onOpenClassMembersSettings}
            onOpenClassLessonAlarmSettings={onOpenClassLessonAlarmSettings}
            onOpenWorksheetTemplateRegistry={onOpenWorksheetTemplateRegistry}
            onOpenTutorials={onOpenTutorials}
            onLogout={onLogout}
          />
        ) : null}
      </div>
    </header>
  );
};

export default TopNav;
